import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import type { ModelConfig } from '@ownyou/ab-testing';
import {
  MODEL_ROUTE_DEFINITIONS,
  MODEL_ROUTE_GROUPS,
  isLlmRouteExecutableProvider,
  normalizeLlmProviderId,
  type LlmProviderId,
  type ModelRouteId,
  type ModelRouteSelections,
} from '../../lib/llm-routing';
import { RouteModelPickerCard } from './RouteModelPickerCard';

interface SettingsAdvancedAiRoutingSectionProps {
  models: ModelConfig[];
  routeSelections: ModelRouteSelections;
  routeProviders: Record<ModelRouteId, LlmProviderId | ''>;
  setRouteProviders: Dispatch<SetStateAction<Record<ModelRouteId, LlmProviderId | ''>>>;
  setRouteSelection: (routeId: ModelRouteId, selection: { provider: string; model: string }) => void;
  clearRouteSelection: (routeId: ModelRouteId) => void;
  modelsLoading: boolean;
  refreshModels: () => Promise<void>;
}

const PROVIDER_NAMES: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google',
  groq: 'Groq',
  deepinfra: 'DeepInfra',
  xai: 'xAI',
  ollama: 'Ollama',
  webllm: 'WebLLM',
};

function formatPricing(model: { pricing?: { inputPer1M: number; outputPer1M: number } | null }): string {
  if (!model.pricing) return ' [budget reserve]';
  const { inputPer1M: input, outputPer1M: output } = model.pricing;
  if (typeof input !== 'number' || typeof output !== 'number') return '';
  if (input === 0 && output === 0) return ' [FREE]';
  return ` [$${input.toFixed(2)}/$${output.toFixed(2)}]`;
}

export function SettingsAdvancedAiRoutingSection({
  models,
  routeSelections,
  routeProviders,
  setRouteProviders,
  setRouteSelection,
  clearRouteSelection,
  modelsLoading,
  refreshModels,
}: SettingsAdvancedAiRoutingSectionProps) {
  const [applyAllModels, setApplyAllModels] = useState(false);
  const [bulkModelProvider, setBulkModelProvider] = useState<LlmProviderId | ''>('');
  const [bulkModelId, setBulkModelId] = useState('');

  const providers = useMemo(() => {
    const normalized = models
      .map((model) => normalizeLlmProviderId(model.provider))
      .filter((provider): provider is LlmProviderId => Boolean(provider));
    return [...new Set(normalized)];
  }, [models]);

  const commonModel = useMemo(() => {
    const firstRoute = MODEL_ROUTE_DEFINITIONS[0];
    if (!firstRoute) return null;
    const first = routeSelections[firstRoute.id];
    if (!first) return null;
    const allMatch = MODEL_ROUTE_DEFINITIONS.every((route) => {
      const selection = routeSelections[route.id];
      return selection?.provider === first.provider && selection.model === first.model;
    });
    return allMatch ? models.find((model) => (
      model.model === first.model &&
      normalizeLlmProviderId(model.provider) === first.provider
    )) ?? null : null;
  }, [models, routeSelections]);

  const applyModelToAllRoutes = useCallback((modelId: string) => {
    const selectedModel = models.find((model) => (
      model.model === modelId &&
      (!bulkModelProvider || normalizeLlmProviderId(model.provider) === bulkModelProvider)
    ));
    const provider = selectedModel
      ? normalizeLlmProviderId(selectedModel.provider)
      : normalizeLlmProviderId(bulkModelProvider);
    if (!provider || !isLlmRouteExecutableProvider(provider)) {
      console.warn('[Settings] Cannot apply model to all routes with unsupported provider', {
        modelId,
        provider: selectedModel?.provider ?? bulkModelProvider,
      });
      return;
    }
    for (const route of MODEL_ROUTE_DEFINITIONS) {
      setRouteSelection(route.id, { provider, model: modelId });
    }
    setRouteProviders((prev) => {
      const next = { ...prev };
      for (const route of MODEL_ROUTE_DEFINITIONS) {
        next[route.id] = provider;
      }
      return next;
    });
  }, [bulkModelProvider, models, setRouteProviders, setRouteSelection]);

  const bulkModelsForProvider = useMemo(
    () => bulkModelProvider
      ? models.filter((model) => normalizeLlmProviderId(model.provider) === bulkModelProvider)
      : [],
    [bulkModelProvider, models],
  );

  const setRouteProvider = useCallback((routeId: ModelRouteId, provider: LlmProviderId | '') => {
    setRouteProviders((prev) => ({ ...prev, [routeId]: provider }));
  }, [setRouteProviders]);

  return (
    <>
      <p className="text-xs text-neutral-500 mb-3">
        Defaults are automatic. Explicit preferences override a route only after you choose both provider and model.
      </p>
      {modelsLoading && (
        <div className="flex items-center gap-2 text-sm text-neutral-500 p-3 bg-neutral-50 rounded-lg mb-3">
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading models from providers...
        </div>
      )}
      <div className="space-y-5">
        <div className="p-3 bg-neutral-50 rounded-lg border border-neutral-200">
          <label className="flex items-center gap-3 text-sm font-medium text-neutral-900">
            <input
              data-testid="apply-all-models-checkbox"
              type="checkbox"
              checked={applyAllModels}
              onChange={(event) => {
                const checked = event.target.checked;
                setApplyAllModels(checked);
                if (checked && commonModel) {
                  setBulkModelProvider(normalizeLlmProviderId(commonModel.provider) ?? '');
                  setBulkModelId(commonModel.model);
                }
              }}
              className="w-4 h-4 rounded text-primary-500 focus:ring-primary-500"
            />
            Apply selected model to all operations
          </label>
          {applyAllModels && (
            <div className="grid grid-cols-2 gap-2 mt-3">
              <select
                data-testid="apply-all-model-provider"
                value={bulkModelProvider}
                onChange={(event) => {
                  setBulkModelProvider(event.target.value as LlmProviderId | '');
                  setBulkModelId('');
                }}
                className="w-full p-2 bg-white border border-neutral-200 rounded-lg text-sm text-neutral-900"
              >
                <option value="">Select provider...</option>
                {providers.map((provider) => {
                  const executable = isLlmRouteExecutableProvider(provider);
                  return (
                    <option key={provider} value={provider} disabled={!executable}>
                      {PROVIDER_NAMES[provider] || provider}{executable ? '' : ' (blocked)'}
                    </option>
                  );
                })}
              </select>
              <select
                data-testid="apply-all-model-select"
                value={bulkModelId}
                onChange={(event) => {
                  const modelId = event.target.value;
                  setBulkModelId(modelId);
                  if (modelId) applyModelToAllRoutes(modelId);
                }}
                disabled={!bulkModelProvider || !isLlmRouteExecutableProvider(bulkModelProvider) || modelsLoading}
                className="w-full p-2 bg-white border border-neutral-200 rounded-lg text-sm text-neutral-900 disabled:opacity-50"
              >
                <option value="">Select model...</option>
                {bulkModelsForProvider.map((model) => (
                  <option key={model.model} value={model.model}>
                    {model.displayName}{formatPricing(model)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        {MODEL_ROUTE_GROUPS.map((group) => {
          const routes = MODEL_ROUTE_DEFINITIONS.filter((route) => route.group === group.id);
          return (
            <div key={group.id} className="space-y-3">
              <div>
                <h3 className="text-sm font-semibold text-neutral-900">{group.label}</h3>
                <p className="text-xs text-neutral-500">{group.description}</p>
              </div>
              {routes.map((route) => (
                <RouteModelPickerCard
                  key={route.id}
                  route={route}
                  models={models}
                  providers={providers}
                  providerNames={PROVIDER_NAMES}
                  routeProvider={routeProviders[route.id]}
                  selection={routeSelections[route.id]}
                  modelsLoading={modelsLoading}
                  formatPricing={formatPricing}
                  setRouteProvider={setRouteProvider}
                  setRouteSelection={setRouteSelection}
                  clearRouteSelection={clearRouteSelection}
                />
              ))}
            </div>
          );
        })}
      </div>
      <button
        onClick={refreshModels}
        disabled={modelsLoading}
        className="mt-3 px-3 py-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50"
      >
        {modelsLoading ? 'Refreshing...' : 'Refresh model list'}
      </button>
    </>
  );
}
